import { useCallback, useMemo } from 'react'

import { usePendingUploadWorkflow } from 'modules/files/mutations'
import { usePendingUploadStore } from 'modules/files/store/usePendingUploadStore'
import { PERCENT_SCALE } from 'utils/units'

type UseConversationVideoProcessingStateOptions = {
  conversationId: string | undefined
}

const EMPTY_PREVIEW_URLS: string[] = []
const MAXIMUM_IN_PROGRESS_PERCENTAGE = PERCENT_SCALE - 1

export const useConversationVideoProcessingState = (options: UseConversationVideoProcessingStateOptions) => {
  const { conversationId } = options
  const conversationIdFromStore = usePendingUploadStore(state => state.conversationId)
  const pendingUploads = usePendingUploadStore(state => state.pendingUploads)
  const pendingUploadFile = pendingUploads[0]?.file
  const pendingUploadPreviewUrl = pendingUploads[0]?.previewUrl
  const pendingUploadPreviewUrls = useMemo(() => {
    return pendingUploads.map(item => item.previewUrl).filter(Boolean)
  }, [pendingUploads])
  const failUpload = usePendingUploadStore(state => state.failUpload)
  const finishUpload = usePendingUploadStore(state => state.finishUpload)
  const resetUploadState = usePendingUploadStore(state => state.resetUploadState)
  const setUploadProgress = usePendingUploadStore(state => state.setUploadProgress)
  const startUpload = usePendingUploadStore(state => state.startUpload)
  const uploadError = usePendingUploadStore(state => state.uploadError)
  const uploadProgress = usePendingUploadStore(state => state.uploadProgress)
  const uploadState = usePendingUploadStore(state => state.uploadState)
  const { startPendingUpload } = usePendingUploadWorkflow()
  const isMatchingConversation =
    conversationIdFromStore !== undefined && conversationId !== undefined && conversationIdFromStore === conversationId

  const hasPendingUploadForCurrentConversation = useMemo(() => {
    return Boolean(
      isMatchingConversation &&
      pendingUploads.some(item => Boolean(item.file) && Boolean(item.uploadUrl)) &&
      uploadState !== 'ready'
    )
  }, [isMatchingConversation, pendingUploads, uploadState])

  const handleRetry = useCallback(() => {
    if (!conversationId) {
      return
    }

    const retryItems = pendingUploads.filter(item => Boolean(item.uploadUrl))
    if (retryItems.length === 0) {
      return
    }

    resetUploadState()
    startUpload()

    void (async () => {
      const total = retryItems.length
      let completedWeight = 0
      try {
        for (const [index, item] of Array.from(retryItems.entries())) {
          await startPendingUpload(
            {
              conversationId,
              file: item.file,
              fileId: item.fileId,
              uploadUrl: item.uploadUrl,
            },
            {
              onProgress: progress => {
                const overall = Math.round(
                  ((completedWeight + progress.percentage / PERCENT_SCALE) / total) * PERCENT_SCALE
                )
                setUploadProgress({ percentage: Math.min(overall, MAXIMUM_IN_PROGRESS_PERCENTAGE), phase: 'uploading' })
              },
            }
          )
          completedWeight = index + 1
          setUploadProgress({
            percentage: Math.round((completedWeight / total) * PERCENT_SCALE),
            phase: 'uploading',
          })
        }
        finishUpload()
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }
        const message = error instanceof Error ? error.message : 'Upload failed'
        failUpload(message)
      }
    })()
  }, [
    conversationId,
    failUpload,
    finishUpload,
    pendingUploads,
    resetUploadState,
    setUploadProgress,
    startPendingUpload,
    startUpload,
  ])

  return {
    backgroundVideoUrl: pendingUploadPreviewUrl,
    backgroundVideoUrls: isMatchingConversation ? pendingUploadPreviewUrls : EMPTY_PREVIEW_URLS,
    handleRetry,
    hasPendingUploadForCurrentConversation,
    pendingUploadFile,
    uploadError,
    uploadProgress,
    uploading: uploadState === 'uploading',
  }
}
